import { useLayoutEffect } from "react";
const locks = new WeakMap();
// Only live owners are retained. A picker portaled out of the invite tree
// cannot use closest(".mobile-content") on its focused input; reuse the
// already locked route instead of accidentally locking another mounted shell.
const liveOwners = new Set();
function readSnapshot(owner) {
    const style = owner.style;
    return {
        overflow: style.overflow,
        overflowY: style.overflowY,
        overscrollBehavior: style.overscrollBehavior,
        overscrollBehaviorY: style.overscrollBehaviorY,
        touchAction: style.touchAction,
        position: style.position,
        top: style.top,
        left: style.left,
        right: style.right,
        width: style.width,
        scrollY: typeof window === "undefined"
            ? 0
            : window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0,
    };
}
function restoreSnapshot(owner, snapshot) {
    owner.style.overflow = snapshot.overflow;
    owner.style.overflowY = snapshot.overflowY;
    owner.style.overscrollBehavior = snapshot.overscrollBehavior;
    owner.style.overscrollBehaviorY = snapshot.overscrollBehaviorY;
    owner.style.touchAction = snapshot.touchAction;
    // Body/document scrolling on iOS needs the fixed-position variant to avoid
    // rubber-band scrolling underneath a portal. Restore only the properties
    // owned by this lock; a nested lock keeps the snapshot alive until its final
    // release.
    owner.style.position = snapshot.position;
    owner.style.top = snapshot.top;
    owner.style.left = snapshot.left;
    owner.style.right = snapshot.right;
    owner.style.width = snapshot.width;
}
/**
 * Acquire an owned lock for one scroll container.
 *
 * The returned release function is idempotent and only restores the styles
 * captured by this owner when its own nested lock count reaches zero. This is
 * intentionally not a global "unlock body" helper: unrelated modals and route
 * shells retain their locks and inline styles.
 */
export function acquireOwnedScrollLock(owner) {
    if (!owner || typeof document === "undefined")
        return () => { };
    let state = locks.get(owner);
    if (!state) {
        state = { count: 0, snapshot: readSnapshot(owner) };
        locks.set(owner, state);
        liveOwners.add(owner);
    }
    state.count += 1;
    owner.style.overflowY = "hidden";
    owner.style.overscrollBehaviorY = "none";
    owner.style.touchAction = "none";
    // A body lock must also pin the current viewport on iOS Safari. Element
    // scroll owners (the production mobile .mobile-content) stay in flow and
    // only need their own overflow state changed.
    if (owner === document.body || owner === document.documentElement) {
        owner.style.position = "fixed";
        owner.style.top = `-${state.snapshot.scrollY}px`;
        owner.style.left = "0";
        owner.style.right = "0";
        owner.style.width = "100%";
    }
    let released = false;
    return () => {
        if (released)
            return;
        released = true;
        const current = locks.get(owner);
        if (!current)
            return;
        current.count -= 1;
        if (current.count > 0)
            return;
        restoreSnapshot(owner, current.snapshot);
        if (owner === document.body || owner === document.documentElement) {
            // Restore the viewport position after releasing a body fixed lock. Some
            // test DOMs do not implement scrollTo, hence the defensive guard.
            const restoreScroll = window.scrollTo;
            if (current.snapshot.scrollY > 0 && typeof restoreScroll === "function") {
                try {
                    restoreScroll(0, current.snapshot.scrollY);
                }
                catch {
                    // A non-browser DOM may expose scrollTo but reject calls.
                }
            }
        }
        locks.delete(owner);
        liveOwners.delete(owner);
    };
}
/**
 * Locate the production mobile route's scroll owner. Portaled invite dialogs
 * no longer have a useful DOM ancestor, so resolve the owner from the active
 * shell before acquiring the lock. Desktop overlays fall back to the body.
 */
export function findInviteScrollOwner() {
    if (typeof document === "undefined")
        return null;
    const focused = document.activeElement;
    const focusedContent = focused?.closest(".mobile-content");
    if (focusedContent)
        return focusedContent;
    const focusedLayout = focused?.closest(".mobile-layout");
    const layoutContent = focusedLayout?.querySelector(":scope > .mobile-content");
    if (layoutContent)
        return layoutContent;
    // A nested picker takes focus inside a body portal. Keep its token on the
    // same scroll owner as the invite, not the first unrelated mobile shell.
    const activeOwners = Array.from(liveOwners).filter((owner) => owner.isConnected);
    const activeMobileOwner = activeOwners.reverse().find((owner) => owner.classList.contains("mobile-content"));
    if (activeMobileOwner)
        return activeMobileOwner;
    return document.querySelector(".mobile-layout > .mobile-content")
        ?? document.querySelector(".mobile-content")
        ?? document.body;
}
/** Lock the active invite surface for the lifetime of an overlay. */
export function useOwnedScrollLock(active) {
    // Acquire before the portal paints, so iOS cannot scroll the route during
    // the first frame of each open (or between rapid close/reopen cycles).
    useLayoutEffect(() => {
        if (!active)
            return undefined;
        return acquireOwnedScrollLock(findInviteScrollOwner());
    }, [active]);
}
